import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Calendar, Tag } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

interface PatchNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PatchNote {
  version: string;
  date: string;
  type: 'major' | 'minor' | 'patch';
  changes: string[];
}

const PatchNotesModal: React.FC<PatchNotesModalProps> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const [selectedVersion, setSelectedVersion] = useState(0);

  // Mock patch notes data
  const patchNotes: PatchNote[] = [
    {
      version: '1.0.0',
      date: '2024-01-15',
      type: 'major',
      changes: [
        '🎉 Lanzamiento inicial de Zeta AI',
        '✨ Interfaz moderna y minimalista',
        '🌙 Soporte para modo claro y oscuro',
        '🌍 Internacionalización (Español, Portugués, Inglés)',
        '📁 Gestión de archivos con drag & drop',
        '🤖 Generación de imágenes con IA',
        '🎬 Generación de videos con IA',
        '📤 Publicación automática en Tienda Nube',
        '📷 Publicación automática en Instagram',
        '⚙️ Panel de configuración completo'
      ]
    },
    {
      version: '0.9.0',
      date: '2024-01-10',
      type: 'minor',
      changes: [
        '🔧 Mejoras en la interfaz de usuario',
        '🐛 Corrección de errores menores',
        '⚡ Optimización de rendimiento',
        '📱 Mejoras en la responsividad'
      ]
    },
    {
      version: '0.8.5',
      date: '2024-01-05',
      type: 'patch',
      changes: [
        '🐛 Corrección de bug en carga de imágenes',
        '🔒 Mejoras en seguridad',
        '📝 Actualización de documentación'
      ]
    }
  ];

  const getVersionTypeColor = (type: string) => {
    switch (type) {
      case 'major':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'minor':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'patch':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const getVersionTypeIcon = (type: string) => {
    switch (type) {
      case 'major':
        return '🚀';
      case 'minor':
        return '✨';
      case 'patch':
        return '🔧';
      default:
        return '📝';
    }
  };

  const currentNote = patchNotes[selectedVersion];

  const handlePreviousVersion = () => {
    if (selectedVersion < patchNotes.length - 1) {
      setSelectedVersion(selectedVersion + 1);
    }
  };

  const handleNextVersion = () => {
    if (selectedVersion > 0) {
      setSelectedVersion(selectedVersion - 1);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {t('patchNotes')}
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex h-[calc(90vh-120px)]">
              {/* Sidebar - Version List */}
              <div className="w-1/3 border-r border-gray-200 dark:border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Versiones
                </h3>
                <div className="space-y-2">
                  {patchNotes.map((note, index) => (
                    <button
                      key={note.version}
                      onClick={() => setSelectedVersion(index)}
                      className={`w-full text-left p-3 rounded-xl transition-colors duration-200 ${
                        selectedVersion === index
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">v{note.version}</span>
                        <span className={`px-2 py-1 rounded-full text-xs ${getVersionTypeColor(note.type)}`}>
                          {getVersionTypeIcon(note.type)}
                        </span>
                      </div>
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <Calendar size={12} className="mr-1" />
                        {note.date}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Main Content - Version Details */}
              <div className="flex-1 p-6 overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      v{currentNote.version}
                    </h3>
                    <div className="flex items-center space-x-4 mt-2">
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                        <Calendar size={16} className="mr-2" />
                        {currentNote.date}
                      </div>
                      <div className={`flex items-center px-3 py-1 rounded-full text-sm ${getVersionTypeColor(currentNote.type)}`}>
                        <Tag size={14} className="mr-1" />
                        {currentNote.type === 'major' ? 'Lanzamiento Mayor' : 
                         currentNote.type === 'minor' ? 'Actualización Menor' : 'Corrección'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Navigation */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handlePreviousVersion}
                      disabled={selectedVersion >= patchNotes.length - 1}
                      className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      onClick={handleNextVersion}
                      disabled={selectedVersion <= 0}
                      className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>

                {/* Changes List */}
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Cambios en esta versión
                  </h4>
                  {currentNote.changes.map((change, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-xl"
                    >
                      <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0" />
                      <p className="text-gray-700 dark:text-gray-300">{change}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Footer */}
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-slate-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                    Para más información sobre las actualizaciones, visita nuestro centro de ayuda.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PatchNotesModal;
